import React from 'react';
import { useSelect, UseSelectState } from 'downshift';
import styled, { css, useTheme } from 'styled-components/macro';
import { Text } from '../../typography';
import { SPACINGS } from '../../utils/style';
import { IconChevron } from '../../icons';
import { SelectProps } from './Select';
import chroma from 'chroma-js';

const SelectContainer = styled.div`
  position: relative;
  width: ${(p) => p.theme.dimensions.inputWidth};

  .label {
    position: absolute;
    top: 0;
    padding: 0 ${SPACINGS[2]};
    padding-right: ${SPACINGS[2]};
    pointer-events: none;
    transform-origin: left;
    transition: transform 0.2s;

    &.up {
      transform: translateY(0) scale(0.8);
    }

    &.down {
      transform: translateY(${SPACINGS[5]}) scale(1);
    }
  }
`;

type SelectStyledType = {
  isOpen: boolean;
  disabled: boolean;
  isFocused: boolean;
  error: boolean;
  baseColor: string;
  mainColor: string;
  accentColor: string;
  inactiveColor: string;
  dangerColor: string;
  backgroundColor: string;
};

const SelectWrapperStyled = styled.div<SelectStyledType>`
  position: relative;
  width: 100%;
  background-color: ${(p) =>
    p.isFocused || p.isOpen
      ? chroma(p.accentColor).alpha(0.1).hex()
      : chroma(p.accentColor).alpha(0).hex()};
  border-bottom: 1px solid
    ${(p) =>
      p.error
        ? p.dangerColor
        : p.isOpen || p.isFocused
        ? p.accentColor
        : p.baseColor};
  border-radius: 4px 4px 0 0;
  cursor: ${(p) => (p.disabled ? 'not-allowed' : 'auto')};

  & > * {
    cursor: ${(p) => (p.disabled ? 'not-allowed' : 'auto')};
  }

  &:hover {
    ${(p) =>
      !p.disabled &&
      css`
        background-color: ${chroma(p.accentColor).alpha(0.1).hex()};
        outline: none;
        border-bottom: 1px solid ${p.accentColor};
      `}
  }

  transition: border-color 0.15s ease-in-out, background-color 0.15s ease-in-out,
    box-shadow 0.15s ease-in-out;
`;

const SelectStyled = styled.button<SelectStyledType>`
  width: 100%;
  height: 100%;
  background-color: transparent;
  border: none;
  outline: none;
  margin: 0;
  padding: ${SPACINGS[6]} ${SPACINGS[2]} ${SPACINGS[1]};

  &:focus {
    outline: none;
  }
`;

const SelectItemStyled = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 24px;
  margin-right: ${SPACINGS[2]};
`;

const MenuWrapper = styled.ul`
  margin: 0;
  padding: 0;
  border-top: 0;
  outline: none;
  position: absolute;
  right: 0;
  left: 0;
  z-index: ${(p) => p.theme.zIndexes.overlay};
`;

const MenuStyled = styled.div`
  outline: none;
  padding: 0;
  margin: 0;
  margin-top: 1px;
  width: 100%;
  max-height: 10em;
  overflow: auto;
  background-color: ${(p) => p.theme.colors.white};
  box-shadow: ${(p) => p.theme.boxShadows.xs};
  border-radius: 0 0 4px 4px;
  z-index: ${(p) => p.theme.zIndexes.overlay};

  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
`;

const ItemStyled = styled.li<{ bold: boolean; highlighted: boolean }>`
  list-style: none;
  padding: ${SPACINGS[2]};
  background-color: ${(p) =>
    p.highlighted ? p.theme.colors.primary95w : p.theme.colors.white};

  & span {
    font-weight: ${(p) => (p.bold ? 'bold' : 'unset')};
  }
`;

function Select<Item>(props: SelectProps<Item>) {
  const theme = useTheme();
  const {
    id,
    label,
    items,
    initialSelectedItem,
    rowComponent,
    itemToString,
    onItemChange,
    disabled,
    style,
    clearItemAfterChange,
    placeholder,
    error,
    name,
    textColor = theme.colors.label,
    baseColor = theme.colors.secondary80w,
    mainColor = theme.colors.black,
    accentColor = theme.colors.primary,
    inactiveColor = theme.colors.secondary80w,
    dangerColor = theme.colors.danger,
    backgroundColor = theme.colors.white,
  } = props;

  const newId = id ?? name;
  const testid = props.testid ?? `${newId}-select`;

  // to handle controlled and uncontrolled selectedItem
  const selectProps: Partial<UseSelectState<Item>> = {};
  if (typeof props.selectedItem !== 'undefined') {
    selectProps.selectedItem = props.selectedItem;
  }

  const [isFocused, setIsFocused] = React.useState(false);

  const {
    isOpen,
    selectedItem,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    highlightedIndex,
    getItemProps,
  } = useSelect({
    id: newId,
    items,
    itemToString,
    initialSelectedItem,
    stateReducer: (_, actionAndChanges) => {
      const { changes, type } = actionAndChanges;
      if (
        changes.selectedItem &&
        (type === useSelect.stateChangeTypes.ItemClick ||
          type === useSelect.stateChangeTypes.MenuKeyDownEnter)
      ) {
        setTimeout(() => onItemChange(changes.selectedItem));

        if (clearItemAfterChange) {
          return {} as UseSelectState<Item>;
        }
      }
      return changes;
    },
    ...selectProps,
  });

  const isLabelUp = selectedItem || placeholder;

  return (
    <SelectContainer style={style}>
      {label && (
        <Text
          className={`label ${isLabelUp ? 'up' : 'down'}`}
          margin="none"
          type="label"
          htmlFor={name}
          {...getLabelProps()}
        >
          {label}
        </Text>
      )}
      <SelectWrapperStyled
        isOpen={isOpen}
        disabled={Boolean(disabled)}
        isFocused={isFocused}
        error={Boolean(error)}
        baseColor={baseColor}
        mainColor={mainColor}
        accentColor={accentColor}
        inactiveColor={inactiveColor}
        dangerColor={dangerColor}
        backgroundColor={backgroundColor}
      >
        <SelectStyled
          data-testid={`${testid}-button`}
          tabIndex="0"
          type="button"
          isOpen={isOpen}
          disabled={Boolean(disabled)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          {...getToggleButtonProps()}
        >
          <SelectItemStyled>
            {rowComponent ? (
              rowComponent(selectedItem)
            ) : (
              <Text
                margin="none"
                style={selectedItem ? {} : { color: textColor || mainColor }}
              >
                {selectedItem ? itemToString(selectedItem) : placeholder}
              </Text>
            )}
            {isOpen ? (
              <IconChevron
                stroke={disabled ? inactiveColor : accentColor}
                direction="top"
              />
            ) : (
              <IconChevron
                stroke={disabled ? inactiveColor : accentColor}
                direction="bottom"
              />
            )}
          </SelectItemStyled>
        </SelectStyled>
        <MenuWrapper {...getMenuProps()}>
          {isOpen && (
            <MenuStyled>
              {items.map((item, index) => (
                <ItemStyled
                  data-testid={`${testid}-item-${index}`}
                  highlighted={highlightedIndex === index}
                  bold={itemToString(item) === itemToString(selectedItem)}
                  key={`${itemToString(item)}${index}`}
                  {...getItemProps({ item, index })}
                >
                  {rowComponent ? (
                    rowComponent(item)
                  ) : (
                    <Text margin="none">{itemToString(item)}</Text>
                  )}
                </ItemStyled>
              ))}
            </MenuStyled>
          )}
        </MenuWrapper>
      </SelectWrapperStyled>
      {typeof error === 'string' && (
        <Text
          id={`${newId}-error`}
          style={{ color: theme.colors.danger, marginTop: SPACINGS[2] }}
          margin="none"
        >
          {error}
        </Text>
      )}
    </SelectContainer>
  );
}

export default React.memo(Select) as typeof Select;
